import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Printer , CheckCircle2, XCircle, Trash2, Pencil } from 'lucide-react';
import { priceFor, priceHintText, usePriceBook, fetchCatalogue, productLabel } from '../lib/pricing';
import { customerLine } from '../lib/customer';
import { printInvoice, taxLabel, isTaxed } from '../lib/documents';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// An invoice line is either a catalogue product — linked to Products Management
// and priced by the Pricing module — or a free-text custom item for anything not
// in the catalogue (delivery, handling, a one-off supply).
const blankItem = (source = 'catalogue') => ({
  source,
  product_id: '',
  price_id: null,
  product_name: '',
  quantity: 1,
  unit_price: 0,
  amount: 0,
  price_edited: false
});

// Tax comes from the rate picked on the invoice — 0 when the client is not
// charged tax at all.
const withTotals = (items, taxRate = 0) => {
  const subtotal = items.reduce((sum, item) => sum + (item.amount || 0), 0);
  const taxAmount = subtotal * (taxRate || 0) / 100;
  return { subtotal, tax_amount: taxAmount, total_amount: subtotal + taxAmount };
};

const NO_TAX = 'none';




export default function Invoices({ user }) {
  const privs = user?.privileges || [];
  const isAdmin = user?.role?.toLowerCase() === 'admin';
  // finance/admin hold the invoices privilege and validate proformas;
  // sales hold proforma_invoices and may only raise proformas
  const canValidate = isAdmin || privs.includes('*') || privs.includes('invoices');
  const proformaOnly = !canValidate;
  const [invoices, setInvoices] = useState([]);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [taxRates, setTaxRates] = useState([]);
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [defaultTerms, setDefaultTerms] = useState('');
  // The invoice being corrected, or null while raising a new one
  const [editing, setEditing] = useState(null);
  const { priceBook, loadActivePrice } = usePriceBook();
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    order_id: '',
    customer_id: '',
    invoice_type: 'invoice',
    items: [blankItem()],
    subtotal: 0,
    tax_amount: 0,
    total_amount: 0,
    tax_id: null,
    tax_name: null,
    tax_rate: 0,
    payment_account_ids: [],
    payment_terms: '',
    notes: ''
  });

  useEffect(() => {
    fetchInvoices();
    fetchOrders();
    fetchCustomers();
    fetchProducts();
    fetchTaxRates();
    fetchPaymentAccounts();
    fetchInvoiceDefaults();
  }, []);

  const fetchInvoices = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoices(response.data);
    } catch (error) {
      toast.error('Failed to fetch invoices');
    }
  };

  const fetchOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [internalRes, customerRes] = await Promise.allSettled([
        axios.get(`${API}/orders`, { headers }),
        axios.get(`${API}/admin/customer-orders`, { headers })
      ]);
      const internal = internalRes.status === 'fulfilled' ? internalRes.value.data : [];
      // Portal orders become invoiceable once approved; hide ones already invoiced
      const invoiceable = ['approved', 'shipped', 'delivered', 'completed'];
      const customerOrders = (customerRes.status === 'fulfilled' ? customerRes.value.data : [])
        .filter((o) => invoiceable.includes(o.status) && !o.invoice_id);
      setOrders([...internal, ...customerOrders]);
    } catch (error) {
      console.error('Failed to fetch orders');
    }
  };

  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to fetch customers');
    }
  };

  const fetchProducts = async () => {
    setProducts(await fetchCatalogue());
  };

  // Tax rates are maintained in Settings; the one marked default is what a new
  // invoice starts on, and staff can switch it or drop tax altogether.
  const fetchTaxRates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/tax-rates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const active = (response.data || []).filter(t => t.is_active !== false);
      setTaxRates(active);
      const preset = active.find(t => t.is_default);
      if (preset) {
        setFormData(prev => (prev.tax_id ? prev : {
          ...prev,
          tax_id: preset.id,
          tax_name: preset.name,
          tax_rate: preset.rate,
          ...withTotals(prev.items, preset.rate)
        }));
      }
    } catch (error) {
      console.error('Failed to fetch tax rates');
    }
  };

  // Where the customer is to pay. The default account is pre-selected; staff can
  // add the others, or clear them for an invoice that carries no payment details.
  const fetchPaymentAccounts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/payment-accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const active = (response.data || []).filter(a => a.is_active !== false);
      setPaymentAccounts(active);
      const preset = active.find(a => a.is_default);
      if (preset) {
        setFormData(prev => (prev.payment_account_ids?.length
          ? prev
          : { ...prev, payment_account_ids: [preset.id] }));
      }
    } catch (error) {
      console.error('Failed to fetch payment accounts');
    }
  };

  const fetchInvoiceDefaults = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/settings/invoice-defaults`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const terms = response.data?.payment_terms || '';
      setDefaultTerms(terms);
      setFormData(prev => (prev.payment_terms ? prev : { ...prev, payment_terms: terms }));
    } catch (error) {
      console.error('Failed to fetch invoice defaults');
    }
  };

  const toggleAccount = (accountId) => {
    setFormData(prev => {
      const chosen = prev.payment_account_ids || [];
      return {
        ...prev,
        payment_account_ids: chosen.includes(accountId)
          ? chosen.filter(id => id !== accountId)
          : [...chosen, accountId]
      };
    });
  };

  const selectTax = (value) => {
    const rate = taxRates.find(t => t.id === value);
    setFormData(prev => {
      const taxRate = rate ? rate.rate : 0;
      return {
        ...prev,
        tax_id: rate ? rate.id : null,
        tax_name: rate ? rate.name : null,
        tax_rate: taxRate,
        ...withTotals(prev.items, taxRate)
      };
    });
  };

  const addItem = () => {
    const items = [...formData.items, blankItem()];
    setFormData({ ...formData, items, ...withTotals(items, formData.tax_rate) });
  };

  const removeItem = (index) => {
    const items = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items, ...withTotals(items, formData.tax_rate) });
  };

  // Switching a line between a catalogue product and a custom description
  // clears whatever the other mode had filled in.
  const setItemSource = (index, source) => {
    setFormData(prev => {
      const items = [...prev.items];
      items[index] = { ...blankItem(source), quantity: items[index].quantity };
      return { ...prev, items, ...withTotals(items, prev.tax_rate) };
    });
  };

  // Picking a product fills in its name and the price in force for the quantity
  // on the line; the price stays editable for one-off deals.
  const selectProduct = async (index, productId) => {
    const product = products.find(p => p.id === productId);
    if (!product) return;
    const activePrice = await loadActivePrice(product.id);
    setFormData(prev => {
      const items = [...prev.items];
      const item = items[index];
      const { unit_price, price_id } = priceFor(product, activePrice, item.quantity);
      items[index] = {
        ...item,
        source: 'catalogue',
        product_id: product.id,
        product_name: product.name,
        unit_price,
        price_id,
        price_edited: false,
        amount: item.quantity * unit_price
      };
      return { ...prev, items, ...withTotals(items, prev.tax_rate) };
    });
  };

  // Puts a hand-edited line back on the catalogue price.
  const resetItemPrice = (index) => {
    setFormData(prev => {
      const items = [...prev.items];
      const item = items[index];
      const product = products.find(p => p.id === item.product_id);
      if (!product) return prev;
      const { unit_price, price_id } = priceFor(product, priceBook[item.product_id], item.quantity);
      items[index] = { ...item, unit_price, price_id, price_edited: false, amount: item.quantity * unit_price };
      return { ...prev, items, ...withTotals(items, prev.tax_rate) };
    });
  };

  const updateItem = (index, field, value) => {
    setFormData(prev => {
      const items = [...prev.items];
      const numeric = field === 'quantity' || field === 'unit_price';
      let item = { ...items[index], [field]: numeric ? parseFloat(value) || 0 : value };
      // A new quantity can cross a wholesale tier, so re-price catalogue lines
      // unless staff have typed their own price on them.
      if (field === 'quantity' && item.product_id && !item.price_edited) {
        const product = products.find(p => p.id === item.product_id);
        const { unit_price, price_id } = priceFor(product, priceBook[item.product_id], item.quantity);
        item = { ...item, unit_price, price_id };
      }
      if (field === 'unit_price') item.price_edited = true;
      item.amount = item.quantity * item.unit_price;
      items[index] = item;
      return { ...prev, items, ...withTotals(items, prev.tax_rate) };
    });
  };

  const priceHint = (item) =>
    priceHintText(products.find(p => p.id === item.product_id), priceBook[item.product_id], item.quantity);

  // The order is the normal starting point: it brings its customer and its
  // lines across, keeping the product and price record each line was placed on.
  // Every line stays editable afterwards, and lines can be added or removed.
  const handleOrderSelect = (orderId) => {
    const order = orders.find(o => o.id === orderId);
    if (!order) return;
    const items = (order.items || []).map(item => ({
      ...blankItem(item.product_id ? 'catalogue' : 'custom'),
      product_id: item.product_id || '',
      price_id: item.price_id || null,
      product_name: item.product_name || '',
      quantity: item.quantity || 0,
      unit_price: item.unit_price || 0,
      amount: (item.quantity || 0) * (item.unit_price || 0)
    }));
    items.forEach(item => { if (item.product_id) loadActivePrice(item.product_id); });
    setFormData({
      ...formData,
      order_id: orderId,
      customer_id: order.customer_id,
      items,
      ...withTotals(items, formData.tax_rate)
    });
  };

  // A validated proforma has already produced an official invoice — that is the
  // document to correct, so it is not editable here.
  const canEdit = (invoice) => {
    if (invoice.validation_status === 'validated') return false;
    return invoice.invoice_type === 'proforma' ? true : canValidate;
  };

  const openEdit = (invoice) => {
    const items = (invoice.items || []).map(item => ({
      ...blankItem(item.product_id ? 'catalogue' : 'custom'),
      product_id: item.product_id || '',
      price_id: item.price_id || null,
      product_name: item.product_name || '',
      quantity: item.quantity || 0,
      unit_price: item.unit_price || 0,
      amount: item.amount ?? (item.quantity || 0) * (item.unit_price || 0),
      // A price that came off the catalogue is left as it was issued
      price_edited: !!item.product_id
    }));
    items.forEach(item => { if (item.product_id) loadActivePrice(item.product_id); });
    setEditing(invoice);
    setFormData({
      order_id: invoice.order_id || '',
      customer_id: invoice.customer_id || '',
      invoice_type: invoice.invoice_type,
      items: items.length ? items : [blankItem()],
      tax_id: invoice.tax_id || null,
      tax_name: invoice.tax_name || null,
      tax_rate: invoice.tax_rate || 0,
      payment_account_ids: invoice.payment_account_ids
        || (invoice.payment_accounts || []).map(a => a.id).filter(Boolean),
      payment_terms: invoice.payment_terms || '',
      notes: invoice.notes || '',
      ...withTotals(items, invoice.tax_rate || 0)
    });
    setOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_id) {
      toast.error('Please select a customer, or pick an order to take one from');
      return;
    }
    const incomplete = formData.items.some(item =>
      item.source === 'catalogue' ? !item.product_id : !item.product_name.trim()
    );
    if (incomplete) {
      toast.error('Every line needs a product or a description');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      // source and price_edited only steer the form; the saved line keeps the
      // product and price record it came from.
      const payload = {
        ...formData,
        items: formData.items.map(({ source, price_edited, ...item }) => item)
      };
      if (editing) {
        await axios.put(`${API}/invoices/${editing.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(`${editing.invoice_number} updated`);
      } else {
        await axios.post(`${API}/invoices`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Invoice created successfully');
      }
      setOpen(false);
      resetForm();
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${editing ? 'update' : 'create'} invoice`);
    }
  };

  const resetForm = () => {
    setEditing(null);
    const preset = taxRates.find(t => t.is_default);
    const defaultAccount = paymentAccounts.find(a => a.is_default);
    setFormData({
      order_id: '',
      customer_id: '',
      invoice_type: proformaOnly ? 'proforma' : 'invoice',
      items: [blankItem()],
      subtotal: 0,
      tax_amount: 0,
      total_amount: 0,
      tax_id: preset?.id || null,
      tax_name: preset?.name || null,
      tax_rate: preset?.rate || 0,
      payment_account_ids: defaultAccount ? [defaultAccount.id] : [],
      payment_terms: defaultTerms,
      notes: ''
    });
  };

  const getCustomerName = (invoiceOrId) => {
    // Accept either the invoice doc (preferred) or a bare id
    if (invoiceOrId && typeof invoiceOrId === 'object') {
      const inv = invoiceOrId;
      if (inv.customer_details?.name) return inv.customer_details.name;
      const c = customers.find((c) => c.id === inv.customer_id);
      return c ? c.name : 'Unknown';
    }
    const customer = customers.find((c) => c.id === invoiceOrId);
    return customer ? customer.name : 'Unknown';
  };



  const validateProforma = async (invoice) => {
    if (!window.confirm(`Validate ${invoice.invoice_number} and issue the official invoice?`)) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/invoices/${invoice.id}/validate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Validated — official invoice ${res.data.official_invoice.invoice_number} issued`);
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Validation failed');
    }
  };

  const rejectProforma = async (invoice) => {
    const reason = window.prompt(`Reason for rejecting ${invoice.invoice_number}:`);
    if (reason === null) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/invoices/${invoice.id}/reject-validation`, { reason }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Proforma rejected');
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Rejection failed');
    }
  };

  const validationBadge = (invoice) => {
    if (invoice.invoice_type !== 'proforma') {
      return invoice.validated_from_number
        ? <Badge className="bg-blue-50 text-blue-700" variant="outline">from {invoice.validated_from_number}</Badge>
        : null;
    }
    const map = {
      pending: ['bg-amber-100 text-amber-800', 'Pending validation'],
      validated: ['bg-green-100 text-green-800', `Validated → ${invoice.official_invoice_number || ''}`],
      rejected: ['bg-red-100 text-red-800', 'Rejected'],
    };
    const [cls, label] = map[invoice.validation_status] || map.pending;
    return <Badge className={cls} variant="outline" title={invoice.rejection_reason || ''}>{label}</Badge>;
  };

  // Portal orders belong to a customer account rather than the staff customer
  // register, so their name is shown read-only instead of in the picker.
  const selectedOrder = orders.find(o => o.id === formData.order_id);
  const portalCustomer =
    formData.customer_id && !customers.some(c => c.id === formData.customer_id)
      ? selectedOrder?.customer_name || 'Customer from portal order'
      : null;

  return (
    <div data-testid="invoices-page">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Invoices</h1>
          <p className="text-slate-600 mt-1">Manage invoices and proforma invoices</p>
        </div>
        <Dialog open={open} onOpenChange={(next) => { setOpen(next); if (!next) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-invoice-button" onClick={resetForm}>
              <Plus className="mr-2" size={16} />
              Create Invoice
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="invoice-dialog">
            <DialogHeader>
              <DialogTitle>
                {editing ? `Edit ${editing.invoice_number}` : 'Create New Invoice'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="invoice_type">Invoice Type</Label>
                  <Select value={formData.invoice_type} disabled={proformaOnly || !!editing}
                    onValueChange={(value) => setFormData({ ...formData, invoice_type: value })}>
                    <SelectTrigger data-testid="invoice-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {!proformaOnly && <SelectItem value="invoice">Invoice</SelectItem>}
                      <SelectItem value="proforma">Proforma Invoice</SelectItem>
                    </SelectContent>
                  </Select>
                  {proformaOnly && (
                    <p className="text-xs text-gray-400">You raise proforma invoices; the finance team validates them into official invoices.</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="order">Select Order</Label>
                  <Select value={formData.order_id} onValueChange={handleOrderSelect}>
                    <SelectTrigger data-testid="order-select">
                      <SelectValue placeholder="Select order" />
                    </SelectTrigger>
                    <SelectContent>
                      {orders.map((order) => (
                        <SelectItem key={order.id} value={order.id}>
                          {order.order_number}{order.customer_name ? ` — ${order.customer_name}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">Brings the customer and the order lines across.</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="customer">Customer *</Label>
                {portalCustomer ? (
                  <Input value={portalCustomer} disabled data-testid="customer-from-order" />
                ) : (
                  <Select value={formData.customer_id}
                    onValueChange={(value) => setFormData({ ...formData, customer_id: value })}>
                    <SelectTrigger data-testid="customer-select">
                      <SelectValue placeholder="Select customer" />
                    </SelectTrigger>
                    <SelectContent>
                      {customers.map((customer) => (
                        <SelectItem key={customer.id} value={customer.id}>
                          {customerLine(customer)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div className="space-y-2">
                <Label>Invoice Items</Label>
                {formData.items.map((item, index) => (
                  <div key={index} className="rounded-md border border-slate-200 p-2 space-y-1" data-testid={`invoice-item-${index}`}>
                    <div className="flex flex-wrap gap-2 items-end">
                      <div className="w-32">
                        <Select value={item.source} onValueChange={(value) => setItemSource(index, value)}>
                          <SelectTrigger data-testid={`item-source-${index}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="catalogue">Product</SelectItem>
                            <SelectItem value="custom">Custom item</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex-1 min-w-[12rem]">
                        {item.source === 'catalogue' ? (
                          <Select value={item.product_id} onValueChange={(value) => selectProduct(index, value)}>
                            <SelectTrigger data-testid={`product-select-${index}`}>
                              <SelectValue placeholder="Select product" />
                            </SelectTrigger>
                            <SelectContent>
                              {products.length === 0 ? (
                                <div className="px-2 py-3 text-sm text-slate-500">No products available</div>
                              ) : (
                                products.map((product) => (
                                  <SelectItem key={product.id} value={product.id}>
                                    {productLabel(product)}
                                  </SelectItem>
                                ))
                              )}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input
                            placeholder="Description"
                            value={item.product_name}
                            onChange={(e) => updateItem(index, 'product_name', e.target.value)}
                            data-testid={`custom-item-${index}`}
                            required
                          />
                        )}
                      </div>
                      <div className="w-20">
                        <Input
                          type="number"
                          placeholder="Qty"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                          min="1"
                          required
                        />
                      </div>
                      <div className="w-28">
                        <Input
                          type="number"
                          placeholder="Price"
                          value={item.unit_price}
                          onChange={(e) => updateItem(index, 'unit_price', e.target.value)}
                          min="0"
                          step="0.01"
                          required
                        />
                      </div>
                      <div className="w-28">
                        <Input
                          type="number"
                          placeholder="Amount"
                          value={item.amount.toFixed(2)}
                          disabled
                        />
                      </div>
                      {formData.items.length > 1 && (
                        <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(index)}>
                          <Trash2 size={16} />
                        </Button>
                      )}
                    </div>
                    {item.product_id && (
                      <div className="flex justify-between items-center gap-2 text-xs text-slate-500 px-1">
                        <span data-testid={`price-hint-${index}`}>{priceHint(item)}</span>
                        {item.price_edited && (
                          <button
                            type="button"
                            className="text-blue-600 hover:underline whitespace-nowrap"
                            onClick={() => resetItemPrice(index)}
                            data-testid={`reset-price-${index}`}
                          >
                            Use catalogue price
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={addItem}>
                  <Plus size={14} className="mr-1" /> Add Item
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Payment Accounts</Label>
                  {paymentAccounts.length === 0 ? (
                    <p className="text-xs text-slate-500">
                      None configured — add the company's accounts in Settings to show them on invoices.
                    </p>
                  ) : (
                    <div className="space-y-1 border rounded-md p-2 max-h-32 overflow-y-auto">
                      {paymentAccounts.map((account) => (
                        <label key={account.id} className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={(formData.payment_account_ids || []).includes(account.id)}
                            onChange={() => toggleAccount(account.id)}
                            data-testid={`account-checkbox-${account.id}`}
                          />
                          <span>{account.label}</span>
                          <span className="text-xs text-slate-500">
                            {account.account_number || account.provider || ''}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="payment_terms">Payment Terms</Label>
                  <Input
                    id="payment_terms"
                    data-testid="payment-terms-input"
                    value={formData.payment_terms}
                    onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
                    placeholder="e.g., Net 30 days"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Input
                    id="notes"
                    data-testid="notes-input"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  />
                </div>
              </div>

              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between items-center gap-4">
                  <Label htmlFor="tax">Tax</Label>
                  <div className="w-72">
                    <Select value={formData.tax_id || NO_TAX}
                      onValueChange={(value) => selectTax(value === NO_TAX ? null : value)}>
                      <SelectTrigger data-testid="tax-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NO_TAX}>No tax — client not charged</SelectItem>
                        {taxRates.map((tax) => (
                          <SelectItem key={tax.id} value={tax.id}>
                            {tax.name} — {tax.rate}%{tax.description ? ` (${tax.description})` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span data-testid="subtotal">Tsh {formData.subtotal.toFixed(2)}</span>
                </div>
                {isTaxed(formData) && (
                  <div className="flex justify-between">
                    <span>{taxLabel(formData)}:</span>
                    <span data-testid="tax-amount">Tsh {formData.tax_amount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold">
                  <span>Total:</span>
                  <span data-testid="total-amount">Tsh {formData.total_amount.toFixed(2)}</span>
                </div>
              </div>

              <Button data-testid="save-invoice-button" type="submit" className="w-full">
                {editing ? 'Save Changes' : 'Create Invoice'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white rounded-lg border border-slate-200">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Invoice Number</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Total Amount</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-slate-500">
                  No invoices found. Create your first invoice.
                </TableCell>
              </TableRow>
            ) : (
              invoices.map((invoice) => (
                <TableRow key={invoice.id} data-testid={`invoice-row-${invoice.id}`}>
                  <TableCell className="font-medium">{invoice.invoice_number}</TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {invoice.invoice_type === 'proforma' ? 'Proforma' : 'Invoice'}
                    </Badge>
                  </TableCell>
                  <TableCell>{getCustomerName(invoice)}</TableCell>
                  <TableCell>Tsh {invoice.total_amount.toFixed(2)}</TableCell>
                  <TableCell>{new Date(invoice.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>{validationBadge(invoice)}</TableCell>
                  <TableCell className="text-right">
                    {canValidate && invoice.invoice_type === 'proforma' && (invoice.validation_status || 'pending') === 'pending' && (
                      <>
                        <Button data-testid={`validate-invoice-${invoice.id}`} variant="ghost" size="icon"
                          title="Validate — issue official invoice" onClick={() => validateProforma(invoice)}>
                          <CheckCircle2 size={16} className="text-green-600" />
                        </Button>
                        <Button data-testid={`reject-invoice-${invoice.id}`} variant="ghost" size="icon"
                          title="Reject proforma" onClick={() => rejectProforma(invoice)}>
                          <XCircle size={16} className="text-red-600" />
                        </Button>
                      </>
                    )}
                    {canEdit(invoice) && (
                      <Button data-testid={`edit-invoice-${invoice.id}`} variant="ghost" size="icon"
                        title="Edit invoice" onClick={() => openEdit(invoice)}>
                        <Pencil size={16} />
                      </Button>
                    )}
                    <Button
                      data-testid={`print-invoice-${invoice.id}`}
                      variant="ghost"
                      size="icon"
                      onClick={() => printInvoice(invoice, { customers, paymentAccounts, defaultTerms })}
                    >
                      <Printer size={16} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
